import { BarChart, Box, Button, Flex, Loading, Text, useAppTheme } from 'ui'
import { useMemo } from 'react'
import { useFillRateByNoticeTimeseries } from 'queries/pulse/useFillRateByNoticeTimeseries'
import { usePulseContext } from '../../context/PulseProvider'
import { formatPercentage } from 'lib/string'
import { groupBy } from 'lodash'
import { useFillRateByNoticeTimeseriesDownloadCsv } from 'queries/pulse/useFillRateByNoticeTimeseriesDownloadCsv'
import { formatTimePeriodDate } from 'pages/PulsePage/utils'

export function FillRateByNoticeTimeseries() {
  const { colors } = useAppTheme()
  const {
    state: { filters },
  } = usePulseContext()

  const { data, isLoading } = useFillRateByNoticeTimeseries(filters)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useFillRateByNoticeTimeseriesDownloadCsv(filters)

  const chartSeries = useMemo(
    () => [
      {
        name: 'Short Notice',
        label: 'Short notice',
        color: colors.error100,
        labelColor: colors.neutrals[400],
      },
      {
        name: 'Long Notice',
        label: 'Long notice',
        color: colors.primary200,
        labelColor: colors.neutrals[400],
      },
    ],
    []
  )

  const chartData = useMemo(() => {
    const groupedByDate = groupBy(data ?? [], 'shift_date')
    return Object.entries(groupedByDate).map(([date, items]) => {
      return {
        shift_date: formatTimePeriodDate(filters.timePeriod, date),
        ...items.reduce((acc, item) => {
          acc[item.notice_category] = item.fill_rate
          acc[`${item.notice_category} shifts requested`] =
            item.shifts_requested
          return acc
        }, {}),
      }
    })
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Fill Rate by Notice
          </Text>
          <Text fontSize={2} color="lightText">
            Fill Rate vs. Long/Short Notice shifts. Short Notice = Shifts posted
            with less than 12 hours notice
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <BarChart
          data={chartData}
          dataKey="shift_date"
          height={450}
          series={chartSeries}
          valueFormatter={formatPercentage}
          withTooltip
          withLegend
          withPointLabels
          withXAxis
          withYAxis
        />
      )}
    </Box>
  )
}
